import React, { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Image as ImageIcon,
  Video,
  Maximize2,
  Loader2,
  Play,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const API_URL = import.meta.env.VITE_AUTH_SERVER_URL || "http://localhost:3001";

function getImageSrc(item) {
  if (item.url.startsWith("http")) return item.url;
  return `${API_URL}${item.url}`;
}


export default function MediaGallery() {
  const [selectedMedia, setSelectedMedia] = useState(null);

  const { data: media = [], isLoading } = useQuery({
    queryKey: ["gallery-media"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/media?section=gallery`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
  });

  const screenshots = media.filter((m) => m.type === "image");
  const videos = media.filter((m) => m.type === "video");

  return (
    <div className="min-h-screen relative">
      {/* Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#1a0a1a]/95 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-[url('https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6915ad56fe64ad9662a36d0b/ebba8cb85_loading_screen_05.png')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-transparent to-[#0a0a0a]/90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0a0a0a_100%)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
              Media Gallery
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Screenshots, videos, and visual development from Knight Arena
            </p>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-10 h-10 text-[#8B0000] animate-spin" />
            </div>
          ) : (
            <Tabs defaultValue="screenshots" className="w-full">
              <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-2 bg-[#1a1a1a]/80 backdrop-blur-sm border border-[#8B0000]/30 mb-8">
                <TabsTrigger
                  value="screenshots"
                  className="data-[state=active]:bg-[#8B0000] data-[state=active]:text-white"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Screenshots ({screenshots.length})
                </TabsTrigger>
                <TabsTrigger
                  value="videos"
                  className="data-[state=active]:bg-[#8B0000] data-[state=active]:text-white"
                >
                  <Video className="w-4 h-4 mr-2" />
                  Videos ({videos.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="screenshots">
                {screenshots.length === 0 ? (
                  <EmptyState label="No screenshots yet. Check back soon!" />
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {screenshots.map((item) => (
                      <ScreenshotCard key={item.id} item={item} onClick={() => setSelectedMedia(item)} />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="videos">
                {videos.length === 0 ? (
                  <EmptyState label="No videos yet. Check back soon!" />
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {videos.map((item) => (
                      <VideoCard key={item.id} item={item} onClick={() => setSelectedMedia(item)} />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          {/* Bottom Note */}
          <div className="mt-16 text-center">
            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8">
              <h3 className="text-2xl font-bold mb-4 text-white">
                Share Your Screenshots
              </h3>
              <p className="text-gray-400 mb-4">
                Captured an epic moment? Share your screenshots and videos with
                the community on our Discord server.
              </p>
              <p className="text-sm text-gray-500">
                All media shown is work in progress and may not represent the
                final product.
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-5xl bg-[#0a0a0a] border-[#8B0000]/30 p-0 overflow-hidden">
          {/* Close button always visible */}
          <button
            onClick={() => setSelectedMedia(null)}
            className="absolute top-3 right-3 z-50 w-8 h-8 flex items-center justify-center rounded-full bg-black/70 border border-white/20 text-white hover:bg-white/20 transition-colors"
            aria-label="Close"
          >
            ✕
          </button>

          {selectedMedia?.type === "image" && (
            <div className="flex flex-col max-h-[90vh]">
              <div className="overflow-auto">
                <img
                  src={getImageSrc(selectedMedia)}
                  alt={selectedMedia.title}
                  className="w-full h-auto object-contain"
                />
              </div>
              {selectedMedia.title && (
                <div className="p-4 border-t border-[#8B0000]/30">
                  <h2 className="text-xl font-bold text-white">{selectedMedia.title}</h2>
                </div>
              )}
            </div>
          )}

          {selectedMedia?.type === "video" && (
            <div className="flex flex-col">
              <video
                src={selectedMedia.url}
                controls
                autoPlay
                className="w-full max-h-[75vh] bg-black"
              />
              {selectedMedia.title && (
                <div className="p-4 border-t border-[#8B0000]/30">
                  <h2 className="text-xl font-bold text-white">{selectedMedia.title}</h2>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScreenshotCard({ item, onClick }) {
  return (
    <Card
      onClick={onClick}
      className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 overflow-hidden hover:border-white/50 transition-all duration-300 hover:scale-105 cursor-pointer group"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={getImageSrc(item)}
          alt={item.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
            <h3 className="text-base font-bold text-white truncate">{item.title}</h3>
            <Maximize2 className="w-4 h-4 text-white shrink-0 ml-2" />
          </div>
        </div>
      </div>
    </Card>
  );
}

function VideoCard({ item, onClick }) {
  const videoRef = useRef(null);

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 1;
    }
  };

  return (
    <Card
      onClick={onClick}
      className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 overflow-hidden hover:border-white/50 transition-all duration-300 hover:scale-105 cursor-pointer group"
    >
      <div className="relative aspect-video overflow-hidden bg-black">
        <video
          ref={videoRef}
          src={item.url}
          muted
          preload="metadata"
          onLoadedMetadata={handleLoadedMetadata}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
        />
        {/* Play button overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
          <div className="w-14 h-14 rounded-full bg-[#8B0000]/80 border border-white/20 flex items-center justify-center group-hover:bg-[#8B0000] transition-colors shadow-lg">
            <Play className="w-6 h-6 text-white ml-1" />
          </div>
          {item.title && (
            <p className="mt-3 text-sm font-medium text-white px-4 text-center line-clamp-1 drop-shadow">{item.title}</p>
          )}
        </div>
      </div>
    </Card>
  );
}

function EmptyState({ label }) {
  return (
    <div className="text-center py-20">
      <ImageIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
      <p className="text-gray-500">{label}</p>
    </div>
  );
}
